import { useEffect, useState } from "react";

import { ValueSetExpansionContains } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { NativeSelect, Stack, Textarea, TextInput } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";

import { ClearableYesNoRadioField } from "@/components/ClearableYesNoRadioField";
import { MEMBRANES_DATE_TIME_LINK_ID } from "@/encounters/components/EncounterLabor/flowsheet-forms/membranes/constants/membranes-link-ids";
import type { MembranesFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/membranes/types/membranes-form";
import {
  isOtherNativeSelectValue,
  valueSetContainsToNativeSelectOptions,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import { positiveNegativeRadioOptions } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-positive-negative-coding";
import { LaborFlowsheetDateTimeFields } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetDateTimeFields";
import { LaborFlowsheetSignatureInitialsField } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetSignatureInitialsField";
import { QuiltedHealthValueSets } from "@/fhir/enums/quilted-health";
import { questionnaireFieldTestId } from "@/intake-forms/utils/questionnaire-field-testid";
import { getValueSetOptions } from "@/utils/medplum";
import { toast } from "@/utils/notifications";

interface MembranesFormFieldsProps {
  form: UseFormReturnType<MembranesFlowsheetFormValues>;
  clientId: string;
  mode?: "quick-add" | "edit";
  isDrawerOpened?: boolean;
}

export function MembranesFormFields({
  form,
  clientId,
  mode = "quick-add",
  isDrawerOpened = true,
}: MembranesFormFieldsProps) {
  const medplum = useMedplum();
  const [fluidColorOptions, setFluidColorOptions] = useState<ValueSetExpansionContains[]>([]);
  const showFluidColorOther = isOtherNativeSelectValue(form.values.fluidColor);
  const fluidColorInputProps = form.getInputProps("fluidColor");

  useEffect(() => {
    if (!isDrawerOpened) {
      return;
    }

    let isCancelled = false;

    const loadFluidColorOptions = async () => {
      try {
        const fluidColorValueSet = await getValueSetOptions(
          medplum,
          QuiltedHealthValueSets.MembraneFluidColor,
        );
        if (isCancelled) {
          return;
        }
        setFluidColorOptions(fluidColorValueSet ?? []);
      } catch (error) {
        console.error("Error loading membranes value sets:", error);
        if (!isCancelled) {
          toast({
            type: "error",
            title: "Failed to load form options",
            message:
              "Fluid color options could not be loaded. Try closing and reopening the drawer.",
            autoClose: false,
          });
        }
      }
    };

    void loadFluidColorOptions();

    return () => {
      isCancelled = true;
    };
  }, [isDrawerOpened, medplum]);

  return (
    <Stack gap="lg">
      <LaborFlowsheetDateTimeFields
        form={form}
        dateFieldName="examDate"
        timeFieldName="examTime"
        autoPopulateDateTime={mode === "quick-add"}
        dateTimeGroupLinkId={MEMBRANES_DATE_TIME_LINK_ID}
      />

      <NativeSelect
        label="Fluid Color"
        data-path="membranesFluidColor"
        data={valueSetContainsToNativeSelectOptions(fluidColorOptions)}
        {...fluidColorInputProps}
        value={fluidColorInputProps.value ?? ""}
        data-testid={questionnaireFieldTestId("membranesFluidColor")}
      />

      {showFluidColorOther ? (
        <TextInput
          label="Fluid Color Other"
          data-path="membranesFluidColorOther"
          {...form.getInputProps("fluidColorOther")}
          data-testid={questionnaireFieldTestId("membranesFluidColorOther")}
        />
      ) : null}

      <ClearableYesNoRadioField
        form={form}
        name="pooling"
        label="Pooling"
        dataPath="membranesPooling"
        dataTestId={questionnaireFieldTestId("membranesPooling")}
      />

      <ClearableYesNoRadioField
        form={form}
        name="nitrazine"
        label="Nitrazine"
        dataPath="membranesNitrazine"
        options={positiveNegativeRadioOptions}
        dataTestId={questionnaireFieldTestId("membranesNitrazine")}
      />

      <ClearableYesNoRadioField
        form={form}
        name="fernResults"
        label="Fern Results"
        dataPath="membranesFernResults"
        options={positiveNegativeRadioOptions}
        dataTestId={questionnaireFieldTestId("membranesFernResults")}
      />

      <Textarea
        label="Notes"
        data-path="membranesNotes"
        {...form.getInputProps("notes")}
        data-testid={questionnaireFieldTestId("membranesNotes")}
      />

      <LaborFlowsheetSignatureInitialsField
        form={form}
        clientId={clientId}
        label="Sign Initials"
        name="signatureInitials"
        autoPopulateSignature={mode === "quick-add"}
      />
    </Stack>
  );
}
